interface LocalDate {
  year: number;
  month: number;
  day: number;
}

/**
 * If date is a DateObject, interprets that as a local date and returns a Date object,
 * otherwise, returns x as-is without any parsing so the consumer can choose how to interpret it
 */
export function parseDateObject(date: LocalDate): Date;
export function parseDateObject<T extends Date | number | string | null | undefined>(date: T | LocalDate): T | Date;
export function parseDateObject<T extends Date | number | string | null | undefined>(date: T | LocalDate): T | Date {
  if (date instanceof Date || typeof date === 'string' || typeof date === 'number' || !date) {
    return date;
  }
  return new Date(date.year, date.month - 1, date.day);
}

export type DateLike = LocalDate | Date | number | string;

export function parseDate(date: LocalDate | Date | number | string): Date;
export function parseDate(date: LocalDate | Date | number | string | null): Date | null;
export function parseDate(date: LocalDate | Date | number | string | undefined): Date | undefined;
export function parseDate(date: LocalDate | Date | number | string | null | undefined): Date | null | undefined {
  if (date == null || !date) {
    return null;
  }

  if (date instanceof Date) {
    return date;
  }

  date = parseDateObject(date);

  // Special formats that are treated differently by Date object:
  // YYYY-MM-DD : new Date() will consider this UTC-0, we instead consider it local time
  if (typeof date === 'string') {
    if (isNaN(new Date(date).getTime())) {
      return null;
    }
    {
      const dashesUS = /\d{4}-d{2}-d{2}/.exec(date);
      if (dashesUS) {
        const [, year, month, day] = dashesUS;
        return new Date(Number.parseInt(day), Number.parseInt(month) - 1, Number.parseInt(year));
      }
    }
  }
  return new Date(date);
}

export const NO_DATE_INFO_MSG = 'No date information provided';
const DATE_FORMATTER_LONG = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  timeZone: 'UTC',
});
const DATE_FORMATTER_SPELLED_OUT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});
const DATE_FORMATTER_NUMBERS = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'UTC',
});

export function formatDateRange(startDate: DateLike, endDate: DateLike | undefined | null) {
  if (!startDate && !endDate) {
    return NO_DATE_INFO_MSG;
  }
  if (!endDate) {
    return `${formatDate(startDate)}–present`;
  }
  return `${formatDate(startDate)}–${formatDate(endDate)}`;
}

type DateFormat = 'long' | 'spelled-out' | 'numbers';

/** Follows constellation date formats:
 * https://constellation.zillowgroup.com/foundations/content-design/numbers#dates-spelled-out
 *
 * @param format
 * "long": Same as "spelled-out" but with full month name
 * "spelled-out": Abbreviated month, cardinal number date, 4 digits year separated by comma
 * "numbers": MM/DD/YYYY
 */
export function formatDate(date: DateLike | null | undefined, format: DateFormat = 'spelled-out') {
  if (!date) return '';
  const dateObj = parseDate(date);
  return {
    long: DATE_FORMATTER_LONG,
    'spelled-out': DATE_FORMATTER_SPELLED_OUT,
    numbers: DATE_FORMATTER_NUMBERS,
  }[format].format(dateObj);
}

const TIMEZONE_ABBREVIATIONS: Record<string, string> = {
  'America/New_York': 'ET',
  'America/Detroit': 'ET',
  'America/Kentucky/Louisville': 'ET',
  'America/Kentucky/Monticello': 'ET',
  'America/Indiana/Indianapolis': 'ET',
  'America/Indiana/Vincennes': 'ET',
  'America/Indiana/Winamac': 'ET',
  'America/Indiana/Marengo': 'ET',
  'America/Indiana/Petersburg': 'ET',
  'America/Indiana/Vevay': 'ET',
  'America/Chicago': 'CT',
  'America/Indiana/Tell_City': 'CT',
  'America/Indiana/Knox': 'CT',
  'America/Menominee': 'CT',
  'America/North_Dakota/Center': 'CT',
  'America/North_Dakota/New_Salem': 'CT',
  'America/North_Dakota/Beulah': 'CT',
  'America/Denver': 'MT',
  'America/Boise': 'MT',
  'America/Phoenix': 'MT',
  'America/Los_Angeles': 'PT',
  'America/Anchorage': 'AKT',
  'America/Juneau': 'AKT',
  'America/Sitka': 'AKT',
  'America/Metlakatla': 'AKT',
  'America/Yakutat': 'AKT',
  'America/Nome': 'AKT',
  'Pacific/Honolulu': 'HT',
};

/**
 * Representative timezone for each abbreviation, used to get the offset for matching
 */
const ABBR_TZ_IANA_MAP: Record<string, string> = {
  ET: 'America/New_York',
  CT: 'America/Chicago',
  MT: 'America/Denver',
  PT: 'America/Los_Angeles',
  AKT: 'America/Anchorage',
  HT: 'Pacific/Honolulu',
};

function getTimezoneOffset(date: Date, timeZone: string): number {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
  return (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);
}

/**
 * Matches a timezone to an abbreviation based on its offset at the given date
 * @param date - The date to use for calculating the offset (important for DST)
 * @param timeZone - The IANA timezone identifier
 * @returns The matching timezone abbreviation or null if no match found
 */
function getTimezoneAbbreviationByOffset(date: Date, timeZone: string): string | null {
  const targetOffset = getTimezoneOffset(date, timeZone);

  for (const [abbr, representativeZone] of Object.entries(ABBR_TZ_IANA_MAP)) {
    const abbrOffset = getTimezoneOffset(date, representativeZone);
    if (targetOffset === abbrOffset) {
      return abbr;
    }
  }

  return null;
}

/**
 * @param timeZone - The IANA timezone identifier (e.g., 'America/Los_Angeles'). Defaults to user's current timezone.
 */
export function formatDateTime(date: DateLike | null | undefined, timeZone?: string): string {
  if (!date) return '';

  const parsedDate = parseDate(date);
  if (!parsedDate) return '';

  const userTimeZone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  const year = parsedDate.getFullYear();

  let hours = parsedDate.getHours();
  const minutes = String(parsedDate.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';

  hours = hours % 12;
  hours = hours === 0 ? 12 : hours;

  let timezoneStr = TIMEZONE_ABBREVIATIONS[userTimeZone] ?? getTimezoneAbbreviationByOffset(parsedDate, userTimeZone);

  if (!timezoneStr) {
    // Fallback to UTC offset format
    const offsetMinutes = -parsedDate.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes >= 0 ? '+' : '-';
    timezoneStr = `UTC${sign}${offsetHours}${offsetMins > 0 ? `:${String(offsetMins).padStart(2, '0')}` : ''}`;
  }

  return `${month}/${day}/${year} ${hours}:${minutes}${ampm} ${timezoneStr}`;
}
